import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";
import { FilterDropDownType } from "@/utils/utils";
import CustomFilterDropdown from "@/components/CustomFilterDropdown/CustomFilterDropdown";
import "./EndpointGrid.css";
import { Typography, Box } from "@mui/material";
import {
  selectEndpointSummaryRecords,
  fetchEndpointSummaries,
  selectEndpointSummaryRecordLoaded,
  setEndpointSummaryFilterTypes,
  setEndpointSummaryFilterStatus,
  selectEndpointSummaryFilterTypes,
  selectEndpointSummaryFilterStatus,
  endpointTypes,
  OperationProcessState,
  connEndpointStatus,
} from "@/features/connections/integrationSlice";
import { selectIntegrationDateFilter } from "@/features/dateFilterSlice";
import AddEndpointPopup from "./NewEndpoint/AddEndpointPopup";

import axios from "@/features/axiosHelper";

import { createAlert } from "@/features/homepage/alertSliceList";
import _ from "lodash";
import {
  ShepCheckbox,
  ShepPagination,
  ShepTooltip,
  ShepHorizontalMenu,
  ShepSearchBar,
  ShepDataGridPro,
} from "@/components/GenericComponents/ShepComponents";

const getStatus = (status) => {
  // Completed and CompletedWithWarning will both appear as "Connected" status for filters
  if (
    status === OperationProcessState.Completed ||
    status === OperationProcessState.CompletedWithWarning
  ) {
    return loadLabel(OperationProcessState.Completed, connEndpointStatus);
  } else if (
    status === OperationProcessState.FailedWithError ||
    status === OperationProcessState.FailedToBeReattempted
  ) {
    return loadLabel(OperationProcessState.FailedWithError, connEndpointStatus);
  } else return "";
};
const loadLabel = (key, collection) => {
  if (!collection) return "";
  const items = collection.filter((x) => x.key === key);
  if (items && items.length) {
    return items[0].label;
  }
};
const getStatusClass = (status) => {
  switch (status) {
    case 100:
      return "endpoint-summary-status-connected";
    case 101:
      return "endpoint-summary-status-connected";
    case null:
      return "";
    default:
      return "endpoint-summary-status-not-connected";
  }
};

const StatusCell = ({ status, statusMessage }) => {
  const statusClass = getStatusClass(status);
  if (statusMessage === "") {
    statusMessage = "Endpoint enabled, missing connection";
  }

  return (
    <ShepTooltip title={statusMessage}>
      <div className={statusClass}>
        {status === null ? <p>Enabled</p> : null}
      </div>
    </ShepTooltip>
  );
};

const EndpointGrid = (props) => {
  const dispatch = useDispatch();
  const [referenceFilter, setReferenceFilter] = useState("");
  const statusFilter = useSelector(selectEndpointSummaryFilterStatus);
  const typeFilter = useSelector(selectEndpointSummaryFilterTypes);
  const { startDate, endDate } = useSelector(selectIntegrationDateFilter) || {};
  const recordLoaded = useSelector(selectEndpointSummaryRecordLoaded);
  const rows = useSelector(selectEndpointSummaryRecords);
  const [addEndpointOpen, setAddEndpointOpen] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(addEndpointOpen);
  const [selectedEndpointId, setSelectedEndpointId] = useState(-1);
  const [selectedEndpoint, setSelectedEndpoint] = useState({});
  const [connectionSearchText, setConnectionSearchText] = useState(referenceFilter);
  const gridRef = useGridApiRef();

  const columns = [
    {
      field: "EndpointName",
      headerName: "Endpoint",
      width: 230,
    },
    {
      field: "Type",
      headerName: "Type",
      width: 180,
    },
    {
      field: "Status",
      headerName: "Status",
      width: 200,
      renderCell: (params) => {
        if (params.row.IsDisabled) {
          return (
            <ShepTooltip title="Disabled">
              <div>Disabled</div>
            </ShepTooltip>
          );
        }
        let message = getStatus(params.row.Status);
        if (params.row.statusMessage) {
          message += ", ";
          message += params.row.statusMessage;
        }
        return <StatusCell status={params.value} statusMessage={message} />;
      },
    },
    {
      field: "TotalAttempts",
      headerName: "Error Rate",
      width: 320,
      renderCell: (params) => {
        let message = "";
        const failed = params.row.FailedAttempts;
        const total = params.value;
        if (total && total > 0) {
          message =
            failed +
            " of " +
            total +
            " (" +
            Math.round((failed * 100) / total) +
            "%) attempts  failed   ";
        }
        return <Typography>{message}</Typography>;
      },
    },
    {
      field: "LastUpdated",
      headerName: "Last Update",
      flex: 1,
      renderCell: (params) => <Typography>{params.value}</Typography>,
    },
    {
      field: "threedotMenu",
      type: "actions",
      getActions: (params) => [
        <GridActionsCellItem
          label="Edit"
          showInMenu
          onClick={() => {
            // Open add new endpoint for selected endpoint id
            setSelectedEndpointId(params.row.Id);
            setSelectedEndpoint(params.row);
            setAddEndpointOpen(true);
          }}
        />,
        <GridActionsCellItem
          label="Enable"
          showInMenu
          disabled={!params.row.IsDisabled}
          onClick={() => {
            validateEnable({
              endpointId: params.row.EndpointId,
            });
          }}
        />,
        <GridActionsCellItem
          label="Disable"
          showInMenu
          disabled={params.row.IsDisabled}
          onClick={() => {
            validateDisable({
              endpointId: params.row.EndpointId,
            });
          }}
        />,

        <GridActionsCellItem
          label="Delete"
          showInMenu
          onClick={() => validateDelete({ endpointId: params.row.EndpointId })}
        />,
      ],
    },
  ];

  useEffect(() => {
    prepFilters();
  }, []);

  const getStatusFilterString = (args) => {
    const status = statusFilter;

    return status
      ? JSON.stringify(status.filter((x) => x.isChecked).map((x) => x.key))
      : "";
  };

  const getTypeFilterString = (args) => {
    const types = typeFilter;

    return types
      ? JSON.stringify(types.filter((x) => x.isChecked).map((x) => x.key))
      : "";
  };

  const getEndpointData = () => {
    if (!startDate || !endDate) return;

    if (statusFilter.length == 0 || typeFilter.length == 0) {
      return;
    }
    
    const params = {
      statusFilterJson: getStatusFilterString(),
      typeFilterJson: getTypeFilterString(),
      search: referenceFilter,
      startDate: startDate,
      endDate: endDate,
    };
    dispatch(fetchEndpointSummaries(params));
  };

  const debouncedGetEndpointSummary = _.debounce(getEndpointData, 1000);

  useEffect(() => {    
    debouncedGetEndpointSummary();
  }, [typeFilter, statusFilter, startDate, endDate, referenceFilter, dispatch]);
  
  const prepFilters = () => {
    dispatch(setEndpointSummaryFilterTypes({ value: endpointTypes }));
    dispatch(setEndpointSummaryFilterStatus({ value: connEndpointStatus }));
  };

  const schema = [
    {
      id: 0,
      label: "Status",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Status",
      setValue: setEndpointSummaryFilterStatus,
      readVal: selectEndpointSummaryFilterStatus,
      labelKey: "label",
      uniqueKey: "key",
    },
    {
      id: 1,
      label: "Endpoint Type",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Endpoint Type",
      setValue: setEndpointSummaryFilterTypes,
      readVal: selectEndpointSummaryFilterTypes,
      labelKey: "label",
      uniqueKey: "key",
    },
  ];

  const CheckActiveConnections = async ({ endpointIds }) => {
    const response = await axios.get("/endpoint/active-connections", {
      params: { endpointIdsJson: JSON.stringify(endpointIds) },
    });
    return response.data;
  };

  const validateDisable = ({ endpointId = null }) => {
    let endpointIds = [];
    if (endpointId !== null) {
      //single row, from 3 dot menu
      endpointIds = [endpointId];
    } else {
      //batch operation
      const map = gridRef?.current.getSelectedRows();
      const selectedRows = Array.from(map, ([name, value]) => ({ ...value }));
      if (selectedRows.length === 0) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "single",
            title: "No Endpoints Selected.",
            message: "Please select an Endpoint to update.",
          })
        );
        return;
      }
      endpointIds = selectedRows
        .filter((y) => y.IsDisabled === false)
        .map((x) => x.EndpointId);
    }
    if (endpointIds.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "single",
          title: "No Endpoints to Disable.",
          message: "Please select an enabled Endpoint to disable.",
        })
      );
      return;
    }
    dispatch(
      createAlert({
        status: "Info",
        alertType: "result",
        title:
          "Disabling this Endpoint will make it Inactive and Unavailable for Connections.",
        message:
          "Any ongoing process using this endpoint may be interrupted. Are you sure you want to disable this Endpoint?",
        buttonLabel: "Disable",
        onButtonClick: () =>
          handleDisableEnable({
            endpointIds,
            setToDisabled: true,
            heapIOIsBulkAction: (endpointId === null)
          }),
        buttonLabel2: "Cancel",
        //else do nothing
      })
    );
  };

  const validateEnable = ({ endpointId = null }) => {
    let endpointIds = [];
    if (endpointId !== null) {
      //single row, from 3 dot menu
      endpointIds = [endpointId];
    } else {
      //batch operation
      const map = gridRef?.current.getSelectedRows();
      const selectedRows = Array.from(map, ([name, value]) => ({ ...value }));
      if (selectedRows.length === 0) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "single",
            title: "No Endpoints Selected.",
            message: "Please select an Endpoint to update.",
          })
        );
        return;
      }
      endpointIds = selectedRows
        .filter((y) => y.IsDisabled === true)
        .map((x) => x.EndpointId);
    }
    if (endpointIds.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "single",
          title: "No Endpoints to Enable.",
          message: "Please select a disabled Endpoint to enable.",
        })
      );
      return;
    }
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: "This Endpoint is Currently Disabled.",
        message:
          "Enabling it will make it active and available for connections. Are you sure you want to enable this endpoint?",
        buttonLabel: "Enable",
        onButtonClick: () =>
          handleDisableEnable({ endpointIds, setToDisabled: false, heapIOIsBulkAction: (endpointId === null) }),
        buttonLabel2: "Cancel",
      })
    );
  };
  const onTriggerRefresh = () => {
    getEndpointData();
    console.log("setting refresh grid to true", refreshGrid, addEndpointOpen);
  };

  const deleteEndpoint = ({ endpointIds = null, heapIOIsBulkAction }) => {
    if (endpointIds === -1 || endpointIds.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "Endpoint Delete",
          message: "Please select a valid Endpoint to delete!",
          buttonLabel: "Close",
          onClose: onTriggerRefresh,
        })
      );
      console.error("Please select a valid Endpoint to delete!", endpointIds);
      return;
    }
    // Delete selected endpoints and return any that were unsuccessful, then refresh the endpoints table
    axios
      .post("/endpoint/endpoint-deleted", null, {
        params: { endpointIdsJson: JSON.stringify(endpointIds) },
      })
      .then((response) => {
        console.log("Endpoint ids to delete:", endpointIds, response.data);
        const unableToUpdate = response.data;
        const unableToUpdateEndpointIds = Object.keys(unableToUpdate);
        if (unableToUpdateEndpointIds.length === 0) {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "result",
              title: "Endpoint Delete",
              message: "The Endpoint(s) were deleted successfully!",
              buttonLabel: "Close",
              onClose: onTriggerRefresh, // trigger grid refresh with updated data
            })
          );

          // heap.io
          if (heapIOIsBulkAction) {
            window.heap.track("Endpoints - Delete");
          }
        } else {
          console.log("Unable to delete", unableToUpdateEndpointIds);
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Unable to delete Endpoint(s)",
              message:
                "The following Endpoint(s) could not be deleted: " +
                unableToUpdateEndpointIds.map((x) => unableToUpdate[x]),
              buttonLabel: "Close",
              onClose: onTriggerRefresh,
            })
          );
        }
      })
      .catch((error) => console.error(error));
  };

  const validateDelete = ({ endpointId = null }) => {
    let endpointIds = [];
    if (endpointId !== null) {
      //single row, from 3 dot menu
      endpointIds = [endpointId];
    } else {
      //batch operation
      const map = gridRef?.current.getSelectedRows();
      const selectedRows = Array.from(map, ([name, value]) => ({ ...value }));
      if (selectedRows.length === 0) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "single",
            title: "No Endpoints Selected.",
            message: "Please select an Endpoint to delete.",
          })
        );
        return;
      }
      endpointIds = selectedRows.map((x) => x.EndpointId);
    }
    CheckActiveConnections({ endpointIds }).then((activeConnections) => {
      if (activeConnections.length > 0) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            title: "This Endpoint is Currently in Use by an Active Connection",
            message: "You must first disconnect it from all connections.",
            buttonLabel: "Close",
            //buttonLabel2: "Close"
          })
        );
        return;
      }
      console.log("Deleting endpoints:", endpointIds);
      //else
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "This Endpoint will be deleted",
          message: "Are you sure you want to delete this Endpoint?",
          buttonLabel: "Delete",
          // onButtonClick: () => handleDelete({ endpointIds }),
          onButtonClick: () => deleteEndpoint({ endpointIds, heapIOIsBulkAction: (endpointId === null) }),
          buttonLabel2: "Close",
        })
      );
    });
  };

  const handleDisableEnable = ({ endpointIds = [], setToDisabled, heapIOIsBulkAction }) => {
    axios
      .post("/endpoint/endpoint-enabled-status", null, {
        params: { endpointIdsJson: JSON.stringify(endpointIds), setToDisabled },
      })
      .then((response) => {
        const unableToUpdate = response.data;
        const unableToUpdateEndpointIds = Object.keys(unableToUpdate);
        if (unableToUpdateEndpointIds.length === 0) {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              title: "Successfully saved changes.",
              message: "The Endpoint(s) have been updated.",
            })
          );

          // heap.io
          if (heapIOIsBulkAction) {
            if (setToDisabled) {
              window.heap.track("Endpoints - Disable");
            } else {
              window.heap.track("Endpoints - Enable");
            }
          }
        } else {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Unable to save changes.",
              message:
                "The following Endpoint(s) could not be updated: " +
                unableToUpdateEndpointIds.map((x) => unableToUpdate[x]),
            })
          );
        }
      })
      .then(() => {
        //refresh grid
        onTriggerRefresh();
      });
  };

  const successAlert = () => {
    dispatch(
      createAlert({
        status: "Success",
        alertType: "result",
        title: "Submitted successfully",
        message:
          "Order number: SO 564343453 Cloud server setup in progress. This may take 1-5 minutes. Thank you for your patience.",
        //link: "https://www.edisoft.com/",
        buttonLabel: "Button",
        buttonLabel2: "Button2",
      })
    );
  };

  const onAddEndpointClosed = () => {
    setAddEndpointOpen(false);
    getEndpointData();
  };

  const actions = [
    { label: "Enable", onClick: () => validateEnable({}) },
    { label: "Disable", onClick: () => validateDisable({}) },
    {
      label: "Add New Endpoint",
      onClick: () => {
        setAddEndpointOpen(true);
        setSelectedEndpointId(-1);
        setSelectedEndpoint({});
      },
    },
    { label: "Delete", onClick: () => validateDelete({}), variant: "danger" },
  ];

  return (
    <Box sx={{ display: "block", flexDirection: "column" }}>
      <Box style={{ display: "flex", paddingBottom: 5 }}>
        <CustomFilterDropdown schemas={schema} />
        <ShepSearchBar
          placeholder="Search by Endpoint Name, Type or status"
          value={connectionSearchText}
          onChange={(event) => {
            const value = event.target.value;
            setConnectionSearchText(value);

            if (value.trim() === "") {
              setReferenceFilter("");
            }
          }}

          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.defaultPrevented = true;
              setReferenceFilter(connectionSearchText);
            }
          }}

          sx={{
            paddingLeft: "10px",
            width: "480px",
            "&:hover fieldset": {
              backgroundColor: "#ffffff",
            },
            "&.MuiFormControl-root .MuiInputBase-root": {
              borderRadius: "5px",
            },
            "&:hover fieldset": {
              color: "#000000",
            },
          }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <ShepHorizontalMenu actions={actions} />
        </div>
      </Box>
      <AddEndpointPopup
        open={addEndpointOpen}
        onClose={onAddEndpointClosed}
        selectedEndpointId={selectedEndpointId}
        selectedEndpoint={selectedEndpoint}
      />
      <Box
        className="content-box"
        sx={{ display: "flex", flexDirection: "row" }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
            <Box
              sx={{
                height: "78px",
                backgroundColor: "var(--primary-main)",
                color: "var(--default-light)",
                padding: "20px 0px 20px 53px",
                marginTop: "30px",
                borderRadius: "4px",
                border: "1px black",
                boxSizing: "border-box",
              }}
            >
              <Typography
                style={{
                  fontSize: "var(--font-lg)",
                  fontWeight: "var(--font-semi-bold)",
                }}
              >
                Manage Endpoints
              </Typography>
            </Box>

            <ShepDataGridPro
              apiRef={gridRef}
              isDataLoaded={recordLoaded}
              secondsToStall={20}
              rows={rows}
              columns={columns}
              columnHeaderHeight={78}
              getRowId={(row) => row.EndpointId}
              rowHeight={100}
              pagination
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 15, page: 0 },
                },
              }}
              whiteHeader={true}
              checkboxSelection={true}
              disableMultipleRowSelection={true}
              disableColumnMenu={true}
              disableColumnFilter={true}
              disableColumnReorder={true}
              hideFooterSelectedRowCount={true}
              onRowSelectionModelChange={(ids) => {
                setSelectedEndpointId(ids);
              }}
              columnVisibilityModel={{
                targetArtifactDictionaryDataId: false,
                sourceArtifactDictionaryDataId: false,
                sourceConnectionId: false,
                isHeld: false,
                isArchived: false,
              }}
              slots={{
                baseCheckbox: ShepCheckbox,
                pagination: ShepPagination,
              }}
              sx={{
                color: "var(--text-primary)",
                fontSize: "var(--font-sm)",
                fontFamily: "var(--font-family)",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                backgroundColor: "var(--default-light)",
                border: "none",
                "& .MuiDataGrid:hover": {
                  backgroundColor: "inherit", // Or 'transparent' or whatever color you'd like
                },
                "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                  opacity: 0,
                  borderBottom: "none",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "var(--tertiary-13)",
                },
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-pale)",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                  {
                    outline: "none",
                  },
                "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                  {
                    opacity: 1,
                  },
                "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                  opacity: 1,
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "var(--font-md)",
                  fontWeight: "var(--font-bold)",
                  borderLeft: "none",
                },
                "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                  {
                    borderBottomLeftRadius: "4px",
                    borderBottomRightRadius: "4px",
                    backgroundColor: "var(--primary-pale)",
                    borderBottom: "none",
                    borderLeft: "none",
                  },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EndpointGrid;
